import React, { useCallback, useEffect, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import _ from 'lodash'

import LineChart from '../../linechart'
import PaceLoader from '../../paceloader'
import Heading from '../heading'
import RefreshButton from '../refresh_button'
import AnalyticsFilters from '../analytics_filters'
import ReportTableOrg from './report_org_table'
import ReportTableIndividual from './report_individual_table'

import { ApiHelper } from '../../../../helpers/apihelper'
import * as Constants from '../../../../helpers/constants'
import { buildApiFilters, getMetrics, getTableHeading, isOrgAdminView } from '../helpers'

/**
 * Port of cowebapp analytics/components/email_verifier/index.js — Email
 * Verification Usage Reports (report/email_verify endpoint).
 */
const EmailVerifier = ({ client, viewedUser, memberOptions }) => {

  const [metrics, setMetrics] = useState({
    totalVerified: 0,
    bounceDetected: 0,
    estCostSaved: 0
  })
  const [isPerfSummaryOpen, setIsPerfSummaryOpen] = useState(true)
  const [usageStats, setUsageStats] = useState([])
  const [usageReports, setUsageReports] = useState([])
  const [showTable, setShowTable] = useState('individual')
  const [isStatsLoading, setIsStatsLoading] = useState(false)
  const [graphData, setGraphData] = useState(null)
  const [isGraphDataLoading, setIsGraphDataLoading] = useState(false)
  const [filters, setFiltersState] = useState({})

  const summaryToggle = () => {
    setIsPerfSummaryOpen(!isPerfSummaryOpen)
  }

  const getGraphData = useCallback((data) => {
    let stat = ''
    if (Object.prototype.hasOwnProperty.call(data, 'user_stats_org')) {
      stat = data.user_stats_org[0]
    } else {
      stat = data.user_stats[0]
    }
    let graphData = {
      breakdown: data.breakdown,
      labels: [],
      values: [
        {
          key: 'valid', label: 'Valid', data: [], borderColor: '#3cba9f',
          backgroundColor: '#71d1bd0f', fill: true
        },
        {
          key: 'invalid', label: 'Invalid', data: [], borderColor: '#c45850',
          backgroundColor: '#d78f8914', fill: true
        },
        {
          key: 'catch_all', label: 'Catch All', data: [], borderColor: '#ffa500',
          backgroundColor: '#ffc04d0f', fill: true
        },
        {
          key: 'unknown', label: 'Unknown', data: [], borderColor: '#c0c0c0',
          backgroundColor: '#bfbfbf14', fill: true
        },
        {
          key: 'total', label: 'Total', data: [], borderColor: '#3e95cd',
          backgroundColor: '#7bb6dd12', fill: true
        },
      ],
    }

    stat.stats.forEach((data) => {
      graphData.labels.push(data.date)
    })
    graphData.values.forEach((item) => {
      stat.stats.forEach((data) => {
        Object.keys(data).forEach((key) => {
          if (key === item.key) {
            item.data.push(data[key])
          }
        })
      })
    })
    return graphData
  }, [])

  const getGraphOrgData = useCallback((data) => {
    let arrStats = []
    data.user_stats.forEach((data) => {
      data.stats.forEach((st) => {
        arrStats.push(st)
      })
    })
    let objStats = {}
    arrStats.forEach(function (d) {
      if (Object.prototype.hasOwnProperty.call(objStats, d.date)) {
        objStats[d.date] = {
          valid: objStats[d.date].valid + d.valid,
          invalid: objStats[d.date].invalid + d.invalid,
          catch_all: objStats[d.date].catch_all + d.catch_all,
          total: objStats[d.date].total + d.total,
          unknown: objStats[d.date].unknown + d.unknown,
        }
      } else {
        objStats[d.date] = {
          valid: d.valid,
          invalid: d.invalid,
          catch_all: d.catch_all,
          total: d.total,
          unknown: d.unknown,
        }
      }
    })
    var userStatsOrg = [{ stats: [] }]
    for (var prop in objStats) {
      userStatsOrg[0].stats.push({
        date: prop,
        valid: objStats[prop].valid,
        invalid: objStats[prop].invalid,
        catch_all: objStats[prop].catch_all,
        total: objStats[prop].total,
        unknown: objStats[prop].unknown,
      })
    }
    data.user_stats_org = userStatsOrg

    return getGraphData(data)
  }, [getGraphData])

  const calculateBounceRate = (unknown, invalid, total) => {
    let bounceRate = 0
    let count = total - unknown
    if (total !== 0) {
      bounceRate = (Number(invalid) / Number(count)) * 100
    }
    return bounceRate > 0 ? bounceRate : 0
  }

  const calculateEstimatedCostSaved = (count) => {
    let costSaved = 0
    costSaved = Number(count) * Constants.AVG_EMAIL_SENDING_COST
    return costSaved
  }

  const setterMetrics = useCallback((stats, role) => {
    let metrics = {}
    let totalVerified = 0
    let tableData = []
    let invalid = 0
    let unknown = 0

    if (role === 'all') {
      stats.forEach((data) => {
        let total = 0
        let invalid = 0
        let unknown = 0
        data.stats.forEach((stat) => {
          if (stat.total > 0) {
            total = total + stat.total
            invalid = invalid + stat.invalid
            unknown = unknown + stat.unknown
          }
        })
        tableData.push({
          total: total,
          invalid: invalid,
          unknown: unknown,
        })
      })
      tableData.forEach((data) => {
        if (data.total > 0) {
          invalid = invalid + data.invalid
          totalVerified = totalVerified + data.total
          unknown = unknown + data.unknown
        }
      })
    } else {
      stats.forEach((data) => {
        if (data.total > 0) {
          invalid = invalid + data.invalid
          totalVerified = totalVerified + data.total
          unknown = unknown + data.unknown
        }
      })
    }

    metrics.bounceDetected = calculateBounceRate(unknown, invalid, totalVerified)
    metrics.totalVerified = totalVerified
    metrics.estCostSaved = calculateEstimatedCostSaved(invalid)

    return metrics
  }, [])

  const loadReportData = useCallback(() => {
    setIsGraphDataLoading(true)
    setIsStatsLoading(true)

    const { filters: apiFilters, selectedUserState } = buildApiFilters({
      viewedUser,
      memberFilter: filters.members,
      dateRangeFilter: filters.date_range
    })

    const queryParams = _.pick(
      { user_id: viewedUser.id, filters: JSON.stringify(apiFilters) },
      ApiHelper(client, 'EMAIL_VERIFY_REPORTS').API_QUERY
    )

    ApiHelper(client, 'EMAIL_VERIFY_REPORTS').API(client, queryParams)
      .then((res) => {
        setIsGraphDataLoading(false)
        if (res && res.status === Constants.CO_API.SUCCESS) {

          let usageStatsIndividualTable = selectedUserState.value.role === 'all' ?
            res.data.user_stats : res.data.user_stats[0].stats
          setUsageReports(res.data)
          setShowTable(selectedUserState.value.role === 'all' ? 'orgAllData' : 'individual')
          setUsageStats(usageStatsIndividualTable)
          setIsStatsLoading(false)
          let collectGraphData = selectedUserState.value.role === 'all'
            ? getGraphOrgData(res.data) : getGraphData(res.data)
          setGraphData(collectGraphData)
          setMetrics(setterMetrics(usageStatsIndividualTable, selectedUserState.value.role))
        }
      })
      .catch((err) => {
        setIsGraphDataLoading(false)
        setIsStatsLoading(false)
        console.log('UsageReports api erred', err)
      })
  }, [client, viewedUser, filters, getGraphData, getGraphOrgData, setterMetrics])

  useEffect(() => {
    if (!_.isEmpty(filters)) {
      loadReportData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const refreshReports = () => {
    loadReportData()
  }

  const onSetFilters = (filters) => {
    setFiltersState({ ...filters })
  }

  return (
    <div className="analytics-graph-content">
      <div className="report-header col-12 d-flex justify-content-between align-items-center">
        <div className="report-date-filter">
          <Heading
            toolTipMessage="Find your credit utilization of the email verifier service"
            title="Email Verification Usage Reports"
          />
        </div>
      </div>
      <div className="report-header col-12 d-flex justify-content-between align-items-center">
        <AnalyticsFilters
          client={client}
          viewedUser={viewedUser}
          memberOptions={memberOptions}
          filterState={filters}
          onSetFilters={onSetFilters}
        />
        <RefreshButton onRefresh={refreshReports} />
      </div>
      <div className="report-content ps-3 pe-3">
        <PaceLoader isLoading={isStatsLoading} />
        <div>
          <div className="d-flex report-performance-summary g-0 flex-column pt-3">
            <div className="page-report-heading">
              <h3 className="m-0">Summary</h3>
              <div className="btn-toolbar">
                <div className="btn-group">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => summaryToggle()}
                  >
                    {isPerfSummaryOpen === true ? (
                      <i className="fa fa-chevron-up"></i>
                    ) : (
                      <i className="fa fa-chevron-down"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <Collapse in={isPerfSummaryOpen}>
              <div className="graph-content">
                <div className="metrics">
                  <div className="metrics-content">
                    <div className="total-verified">
                      <span>Total Verified</span>
                      {getMetrics(
                        metrics.totalVerified,
                        2,
                        '',
                        '',
                        'Total Verified'
                      )}
                    </div>
                    <div className="bounce-detected">
                      <span>Bounce Detected</span>
                      <span>
                        <div
                          className={`c100 p${Math.round(
                            metrics.bounceDetected
                          )} small orange`}
                        >
                          {getMetrics(
                            metrics.bounceDetected,
                            0,
                            '',
                            '%',
                            'Bounce Detected'
                          )}
                          <div className="slice">
                            <div className="bar"></div>
                            <div className="fill"></div>
                          </div>
                        </div>
                      </span>
                    </div>
                    <div className="est-cost-saved">
                      <span>Estimated Cost Saved</span>
                      {getMetrics(
                        metrics.estCostSaved,
                        2,
                        '$',
                        '',
                        'Estimated Cost Saved'
                      )}
                    </div>
                  </div>
                </div>
                <div className="area-graph">
                  <LineChart
                    graphData={graphData}
                    isGraphDataLoading={isGraphDataLoading}
                    width={300}
                    height={250}
                    position="bottom"
                  />
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <div>
          <div className="d-flex report-performance-breakdown g-0 flex-column">
            <div className="page-report-heading">
              <h3 className="m-0 d-flex align-items-center">
                {getTableHeading(isOrgAdminView(viewedUser) ? 'all' : viewedUser.user_role, usageReports.breakdown)}
              </h3>
            </div>
            {!isStatsLoading &&
              showTable === 'orgAllData'
              ? <ReportTableOrg
                userReportData={usageReports}
                tableData={usageStats}
              />
              : <ReportTableIndividual
                userReportData={usageReports}
                tableData={usageStats}
              />
            }
          </div>
        </div>
        <div className="report-footnote">
          <i
            className="fa fa-exclamation-circle"
            aria-hidden="true"
          >
          </i>
          <small className="form-text">
            Row will be supressed when no activity found for the particular
            day or hour for the chosen period
          </small>
        </div>
      </div>
    </div>
  )
}

export default EmailVerifier
